import { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback } from 'react';
import { MarkerData } from '../types/markerData';
import { MarkerContextProps } from '../interfaces/ContextMarker';
import { pinsService } from '../services';
import { useToast } from './useToast';

// Estendi l'interfaccia per includere più funzionalità
interface ExtendedMarkerContextProps extends MarkerContextProps {
    isLoading: boolean;
    error: string | null;
    createMarker: (markerData: Partial<MarkerData>) => Promise<MarkerData | null>;
    updateMarker: (markerId: string | number, markerData: Partial<MarkerData>) => Promise<MarkerData | null>;
    deleteMarker: (markerId: string | number) => Promise<boolean>;
    refreshMarkers: () => Promise<void>;
}

const MarkerContext = createContext<ExtendedMarkerContextProps | undefined>(undefined);

export const MarkerProvider = ({ children }: { children: ReactNode }) => {
    const [markers, setMarkers] = useState<MarkerData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const userId = sessionStorage.getItem('userId');
    const { markerCreated, markerUpdated, markerDeleted } = useToast();

    // Funzione per caricare i markers con gestione errori migliorata
    const fetchMarkers = useCallback(async (forceRefresh = false) => {
        if (!userId) {
            console.warn('No userId found, skipping marker fetch');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const data = await pinsService.getUserMarkers(userId, forceRefresh);
            // Assicurati che i markers siano sempre un array valido
            setMarkers(Array.isArray(data) ? data : []);
        } catch (error: any) {
            console.error('Error fetching markers:', error);
            setError(error.message || 'Errore nel caricamento dei pin');
            // Non mostriamo un toast qui perché il servizio API lo gestisce già
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    // Carica i markers all'inizializzazione
    useEffect(() => {
        if (userId) {
            fetchMarkers();
        }
    }, [userId]); // Rimuovo fetchMarkers dalle dipendenze per evitare loop

    // Funzione per creare un nuovo marker
    const createMarker = useCallback(async (markerData: Partial<MarkerData>): Promise<MarkerData | null> => {
        setIsLoading(true);
        setError(null);

        try {
            const newMarker = await pinsService.createMarker(markerData);

            // Aggiorna lo stato locale immediatamente
            setMarkers(prev => [...prev, newMarker]);

            markerCreated();

            return newMarker;
        } catch (error: any) {
            console.error('Error creating marker:', error);
            setError(error.message || 'Errore nella creazione del pin');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Funzione per aggiornare un marker
    const updateMarker = useCallback(async (markerId: string | number, markerData: Partial<MarkerData>): Promise<MarkerData | null> => {
        setIsLoading(true);
        setError(null);

        try {
            const updatedMarker = await pinsService.updateMarker(markerId, markerData);

            // Aggiorna lo stato locale immediatamente
            setMarkers(prev => prev.map(m =>
                m.id === markerId ? updatedMarker : m
            ));

            markerUpdated();

            return updatedMarker;
        } catch (error: any) {
            console.error('Error updating marker:', error);
            setError(error.message || 'Errore nell\'aggiornamento del pin');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Funzione per eliminare un marker
    const deleteMarker = useCallback(async (markerId: string | number): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        try {
            await pinsService.deleteMarker(markerId);

            // Aggiorna lo stato locale immediatamente
            setMarkers(prev => prev.filter(m => m.id !== markerId));

            markerDeleted();

            return true;
        } catch (error: any) {
            console.error('Error deleting marker:', error);
            setError(error.message || 'Errore nella cancellazione del pin');
            // Errore gestito dal servizio API
            return false;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Funzione per refreshare i markers forzando il refresh della cache
    const refreshMarkers = useCallback(async () => {
        await fetchMarkers(true);
    }, [fetchMarkers]);

    // Memorizza il valore del context per evitare re-render non necessari
    const value = useMemo(
        () => ({
            markers,
            fetchMarkers,
            isLoading,
            error,
            createMarker,
            updateMarker,
            deleteMarker,
            refreshMarkers
        }),
        [markers, fetchMarkers, isLoading, error, createMarker, updateMarker, deleteMarker, refreshMarkers]
    );

    return (
        <MarkerContext.Provider value={value}>
            {children}
        </MarkerContext.Provider>
    );
};

// Hook personalizzato con controllo del context
export const useMarkers = () => {
    const context = useContext(MarkerContext);
    if (!context) {
        throw new Error('useMarkers must be used within a MarkerProvider');
    }
    return context;
};